import { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Icon, Modal, ModalHeader, ModalBody, ModalFooterWithButtons } from '@evertrue/et-components';
import sanitizeHtml from '../../utils/sanitize-html';

/** "No Signature" sentinel used as the first option in the list. */
const NO_SIGNATURE = { id: 0, signature_name: 'No Signature', signature_body: '' };

/**
 * Shared modal for picking an email signature.
 * Shows each signature with a live HTML preview in a split-pane layout.
 */
const SignaturePickerModal = ({ isOpen, onClose, signatures, onSelect, currentSignatureId }) => {
  const [selectedId, setSelectedId] = useState(currentSignatureId ?? null);

  const allSignatures = [NO_SIGNATURE, ...(signatures || [])];
  const selectedSignature = allSignatures.find((s) => s.id === selectedId) || null;

  return (
    <Modal isOpen={isOpen} closeModal={onClose} size="medium">
      <ModalHeader title="Select Email Signature" closeModal={onClose} />
      <ModalBody>
        <div className="signature-picker">
          <div className="signature-picker--left">
            <div className="signature-picker--list">
              {allSignatures.map((sig) => (
                <button
                  key={sig.id}
                  type="button"
                  className={`signature-picker--item ${selectedId === sig.id ? 'is-selected' : ''}`}
                  onClick={() => setSelectedId(sig.id)}
                >
                  <div className="signature-picker--item-icon">
                    <Icon icon={sig.id === 0 ? 'block' : 'edit'} />
                  </div>
                  <span className="signature-picker--item-name">{sig.signature_name}</span>
                  {selectedId === sig.id && <Icon icon="check" className="signature-picker--item-check" />}
                </button>
              ))}
            </div>
          </div>

          <div className="signature-picker--right">
            {selectedSignature ? (
              <div className="signature-picker--preview">
                <div className="signature-picker--preview-header">
                  <h4 className="signature-picker--preview-title">{selectedSignature.signature_name}</h4>
                </div>
                {selectedSignature.signature_body ? (
                  <div
                    className="signature-picker--preview-body"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(selectedSignature.signature_body) }}
                  />
                ) : (
                  <div className="signature-picker--preview-empty">
                    <Icon icon={selectedSignature.id === 0 ? 'block' : 'edit'} />
                    <p>{selectedSignature.id === 0 ? 'No signature will be applied' : 'No preview available'}</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="signature-picker--preview-empty">
                <Icon icon="edit" />
                <p>Select a signature to preview</p>
              </div>
            )}
          </div>
        </div>
      </ModalBody>
      <ModalFooterWithButtons>
        <Button type="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="action"
          disabled={selectedId == null}
          onClick={() => {
            const sig = allSignatures.find((s) => s.id === selectedId);
            if (sig) onSelect(sig);
          }}
        >
          {selectedId === 0 ? 'Remove Signature' : 'Apply Signature'}
        </Button>
      </ModalFooterWithButtons>
    </Modal>
  );
};

SignaturePickerModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  signatures: PropTypes.array.isRequired,
  onSelect: PropTypes.func.isRequired,
  currentSignatureId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default SignaturePickerModal;
